/**
 * Centralized API Client for Financial App US
 * 
 * Production-grade axios client with:
 * - Environment-based configuration
 * - Request/response interceptors
 * - Error handling
 * - Authentication support (ready for future implementation)
 * - Request/response logging
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { auth } from '../config/firebase';
import { getIdToken } from 'firebase/auth';

// Environment configuration
// Use relative URL (empty string) for production Cloud Run deployment where frontend/backend are same origin
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const MONITOR_BASE_URL = import.meta.env.VITE_MONITOR_BASE_URL ?? '';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');
const MONITOR_TIMEOUT = parseInt(import.meta.env.VITE_MONITOR_TIMEOUT || '10000');
// Authentication is ALWAYS enabled for enterprise features
const ENABLE_AUTH = true; // import.meta.env.VITE_ENABLE_AUTH === 'true';

/**
 * Main API Client for financial operations
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Monitoring API Client (separate service on different port)
 */
export const monitorClient: AxiosInstance = axios.create({
  baseURL: `${MONITOR_BASE_URL}/api/v1`,
  timeout: MONITOR_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Request Interceptors
// ============================================================================

/**
 * Add authentication token to requests (when auth is enabled)
 * Prioritizes Firebase token over legacy auth token
 */
apiClient.interceptors.request.use(
  async (config) => {
    if (ENABLE_AUTH) {
      let token: string | null = null;

      // Always get a FRESH token from Firebase SDK (auto-refreshes expired tokens)
      if (auth.currentUser) {
        try {
          token = await getIdToken(auth.currentUser);
          // Update localStorage so other parts of the app stay in sync
          localStorage.setItem('firebaseToken', token);
          localStorage.setItem('auth_token', token);
        } catch (err) {
          console.warn('[API Client] Failed to refresh Firebase token:', err);
        }
      }

      // Fallback to cached token if Firebase SDK not ready yet
      if (!token) {
        token = localStorage.getItem('firebaseToken') || localStorage.getItem('auth_token');
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Log requests in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

monitorClient.interceptors.request.use(
  (config) => {
    if (import.meta.env.DEV) {
      console.log(`[Monitor Request] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// Response Interceptors
// ============================================================================

/**
 * Handle successful responses and errors
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log responses in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    // Handle specific error cases
    if (error.response) {
      const status = error.response.status;
      const data: any = error.response.data;

      console.error(`[API Error] ${status}`, {
        url: error.config?.url,
        method: error.config?.method,
        error: data,
      });

      // Handle authentication errors - try force-refreshing the token once
      if (status === 401 && ENABLE_AUTH) {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        if (!originalRequest._retry && auth.currentUser) {
          originalRequest._retry = true;
          try {
            const freshToken = await getIdToken(auth.currentUser, true); // force refresh
            localStorage.setItem('firebaseToken', freshToken);
            localStorage.setItem('auth_token', freshToken);
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${freshToken}`;
            }
            return apiClient(originalRequest);
          } catch (refreshError) {
            console.error('[API Client] Token refresh failed:', refreshError);
          }
        }
        localStorage.removeItem('auth_token');
        localStorage.removeItem('firebaseToken');
        window.location.href = '/login';
        return Promise.reject(new Error('Authentication required'));
      }

      // Handle forbidden
      if (status === 403) {
        return Promise.reject(new Error('Access denied'));
      }

      // Handle not found
      if (status === 404) {
        return Promise.reject(new Error('Resource not found'));
      }

      // Handle validation errors
      if (status === 422) {
        const detail = data.detail || 'Validation error';
        return Promise.reject(new Error(detail));
      }

      // Handle server errors
      if (status >= 500) {
        return Promise.reject(new Error('Server error. Please try again later.'));
      }

      // Extract error message from response
      const errorMessage = data.detail || data.message || 'An error occurred';
      return Promise.reject(new Error(errorMessage));
    }

    // Handle network errors
    if (error.request) {
      console.error('[Network Error]', error.message);
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }

    // Handle other errors
    console.error('[Unknown Error]', error);
    return Promise.reject(error);
  }
);

monitorClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    console.error('[Monitor Error]', error.message);
    return Promise.reject(error);
  }
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract error message from API error
 */
export const getErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  return error.message === 'Network Error' || !error.response;
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: any): boolean => {
  return error.response?.status === 401;
};

/**
 * Retry failed request
 */
export const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx)
      if (error instanceof AxiosError && error.response?.status && error.response.status < 500) {
        throw error;
      }

      // Wait before retrying
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }

  throw lastError;
};

/**
 * Create form data for file uploads
 */
export const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();
  
  Object.entries(data).forEach(([key, value]) => {
    if (value instanceof File) {
      formData.append(key, value);
    } else if (value !== null && value !== undefined) {
      formData.append(key, String(value));
    }
  });

  return formData;
};

// Export configured clients
export default apiClient;
